/**
 * OsCountModeEntry — MADE-292
 *
 * Route: /os/count/:sessionId  (public — no Firebase or PIN required)
 *
 * Verifies the count session via GET /api/os/count-sessions/:sessionId/progress.
 * On success: writes CountModeState to localStorage and redirects to
 *   /os/count-mode/ready.
 * On failure: shows an error toast so the staff member knows the session is
 *   invalid or expired.
 *
 * Also serves as the landing page when a count-session QR is scanned via the
 * global useScanListener in OsScanEntry / OsScanItem (those navigate to this
 * route which handles the verify→save→redirect flow).
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { useTenant } from '../../contexts/TenantContext';
import { useCountMode } from '../hooks/useCountMode';
import type { CountModeState } from '../hooks/useCountMode';

interface SessionProgress {
  sessionId: string;
  sessionName: string;
  location: string;
  countedItems: number;
  totalItems: number;
  rosterMode?: 'open' | 'fixed';
  status: 'active' | 'completed' | 'abandoned';
}

export const OsCountModeEntry: React.FC = () => {
  const { sessionId = '' } = useParams<{ sessionId: string }>();
  const { apiBase } = useTenant();
  const { setCountMode } = useCountMode();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) {
      setError('Invalid count session link.');
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(
          `${apiBase}/count-sessions/${encodeURIComponent(sessionId)}/progress`,
        );
        if (cancelled) return;

        if (res.status === 404) {
          setError('Count session not found or expired.');
          return;
        }
        if (!res.ok) {
          setError('Unable to verify count session. Try scanning the QR again.');
          return;
        }

        const data: SessionProgress = await res.json();

        if (data.status !== 'active') {
          setError('This count session is no longer active.');
          return;
        }

        const state: CountModeState = {
          sessionId: data.sessionId,
          sessionName: data.sessionName,
          location: data.location,
          countedItems: data.countedItems,
          totalItems: data.totalItems,
          rosterMode: data.rosterMode,
          enteredAt: new Date().toISOString(),
        };

        setCountMode(state);
        navigate('/os/count-mode/ready', { replace: true });
      } catch {
        if (!cancelled) {
          setError('Network error — check your connection and try again.');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionId, apiBase, setCountMode, navigate]);

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100dvh',
          bgcolor: '#FFF8E1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
        data-testid="count-mode-entry-error"
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        bgcolor: '#FFF8E1',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
      }}
      data-testid="count-mode-entry-loading"
    >
      <CircularProgress sx={{ color: '#F57C00' }} />
      <Typography variant="body1" sx={{ color: '#E65100', fontWeight: 600 }}>
        Verifying count session…
      </Typography>
    </Box>
  );
};
